export const TIMEFRAMES = ["1m", "5m", "15m", "30m", "1h", "4h", "1d"];
export const DEFAULT_SYMBOLS = ["BTC/USDT:USDT", "ETH/USDT:USDT", "SOL/USDT:USDT"];
export const ACTIVE_ORDER_STATES = new Set([
  "new",
  "open",
  "pending",
  "accepted",
  "working",
  "partially_filled",
  "partially filled",
]);

const QUOTE_ASSETS = ["USDT", "USDC", "FDUSD", "BUSD", "USD"];

export const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
export const finite = (value) => Number.isFinite(Number(value));
export const number = (value, fallback = 0) => (finite(value) ? Number(value) : fallback);

export const normalizeSide = (side) => String(side || "").trim().toLowerCase();

export const normalizeSymbol = (symbol = "BTC/USDT:USDT") => {
  const raw = String(symbol || "").trim().toUpperCase();
  const contract = raw.split(":", 1)[0];
  return contract.replace(/[^A-Z0-9]/g, "");
};

export const splitSymbol = (symbol) => {
  const normalized = normalizeSymbol(symbol);
  const quote = QUOTE_ASSETS.find((asset) => normalized.endsWith(asset)) || "";
  return {
    normalized,
    base: quote ? normalized.slice(0, -quote.length) : normalized,
    quote,
  };
};

export const displaySymbol = (symbol) => splitSymbol(symbol).normalized || "—";

export const formatPrice = (value) => {
  if (!finite(value)) return "—";
  const absolute = Math.abs(Number(value));
  const digits = absolute >= 1000 ? 2 : absolute >= 100 ? 3 : absolute >= 1 ? 4 : absolute >= 0.01 ? 6 : 8;
  return Number(value).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: digits,
  });
};

export const formatQty = (value) => {
  if (!finite(value)) return "—";
  return Number(value).toLocaleString(undefined, { maximumFractionDigits: 6 });
};

export const formatTime = (timestamp, timeframe, options = {}) => {
  if (!timestamp) return "—";
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return "—";
  if (timeframe === "1d") {
    return `${date.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      ...options,
      timeZone: "UTC",
    })} UTC`;
  }
  return `${date.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    ...options,
    timeZone: "UTC",
  })} UTC`;
};

export const timeframeToMs = (timeframe) => {
  const match = String(timeframe || "").match(/^(\d+)([mhd])$/);
  if (!match) return 60_000;
  const value = Number(match[1]);
  const unit = match[2];
  const multiplier = unit === "m" ? 60_000 : unit === "h" ? 3_600_000 : 86_400_000;
  return value * multiplier;
};

export const isActiveOrder = (order) =>
  ACTIVE_ORDER_STATES.has(String(order?.status || "").trim().toLowerCase());

export const emaSeries = (values, period) => {
  if (!Array.isArray(values) || !values.length || period <= 0) return [];
  const output = Array(values.length).fill(null);
  if (values.length < period) return output;

  let seed = 0;
  for (let index = 0; index < period; index += 1) {
    if (!finite(values[index])) return output;
    seed += Number(values[index]);
  }
  let previous = seed / period;
  output[period - 1] = previous;
  const alpha = 2 / (period + 1);

  for (let index = period; index < values.length; index += 1) {
    if (!finite(values[index])) {
      output[index] = previous;
      continue;
    }
    previous = Number(values[index]) * alpha + previous * (1 - alpha);
    output[index] = previous;
  }
  return output;
};

export const sessionVwap = (candles, resetDaily = true) => {
  let cumulativePriceVolume = 0;
  let cumulativeVolume = 0;
  let sessionKey = null;

  return (candles || []).map((candle) => {
    const nextKey = resetDaily ? new Date(candle.time).toISOString().slice(0, 10) : "all";
    if (nextKey !== sessionKey) {
      sessionKey = nextKey;
      cumulativePriceVolume = 0;
      cumulativeVolume = 0;
    }
    const typical = (number(candle.high) + number(candle.low) + number(candle.close)) / 3;
    const volume = Math.max(0, number(candle.volume));
    cumulativePriceVolume += typical * volume;
    cumulativeVolume += volume;
    return cumulativeVolume ? cumulativePriceVolume / cumulativeVolume : typical;
  });
};

export const normalizeCandle = (value) => {
  if (!value) return null;
  const candle = Array.isArray(value)
    ? {
        time: Number(value[0]),
        open: Number(value[1]),
        high: Number(value[2]),
        low: Number(value[3]),
        close: Number(value[4]),
        volume: Number(value[5]),
        closed: finite(value[6]) ? Number(value[6]) < Date.now() : true,
      }
    : {
        time: Number(value.time),
        open: Number(value.open),
        high: Number(value.high),
        low: Number(value.low),
        close: Number(value.close),
        volume: Number(value.volume),
        closed: Boolean(value.closed),
      };

  if (![candle.time, candle.open, candle.high, candle.low, candle.close, candle.volume].every(finite)) return null;
  if (candle.time <= 0 || candle.volume < 0) return null;
  if (candle.high < Math.max(candle.open, candle.close, candle.low)) return null;
  if (candle.low > Math.min(candle.open, candle.close, candle.high)) return null;
  return candle;
};

export const normalizeKlines = (rows, limit = 500) => {
  const byTime = new Map();
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    const candle = normalizeCandle(row);
    if (candle) byTime.set(candle.time, candle);
  });
  return Array.from(byTime.values())
    .sort((left, right) => left.time - right.time)
    .slice(-Math.max(1, limit));
};

export const mergeCandles = (current, incoming, limit = 500) =>
  normalizeKlines([...(Array.isArray(current) ? current : []), ...(Array.isArray(incoming) ? incoming : [])], limit);

export const mergeCandle = (current, incoming, limit = 500) => {
  const candle = normalizeCandle(incoming);
  if (!candle) return Array.isArray(current) ? current : [];
  const rows = Array.isArray(current) ? current : [];
  const last = rows[rows.length - 1];
  if (!last) return [candle];
  if (last.time === candle.time) return [...rows.slice(0, -1), candle];
  if (last.time < candle.time) return [...rows, candle].slice(-Math.max(1, limit));
  return mergeCandles(rows, [candle], limit);
};

export const getLiquidationLevel = (position) => {
  const authoritative = [
    position?.liquidation_price,
    position?.liquidationPrice,
    position?.liq_price,
    position?.liqPrice,
  ].find((value) => finite(value) && Number(value) > 0);
  if (authoritative != null) {
    return { value: Number(authoritative), estimated: false };
  }

  const entry = number(position?.entry_price ?? position?.entryPrice);
  const leverage = Math.max(1, number(position?.leverage, 1));
  if (!entry) return null;
  const maintenanceBuffer = 0.005;
  const side = normalizeSide(position?.side);
  if (!["long", "short"].includes(side)) return null;
  const value = side === "long"
    ? Math.max(0, entry * (1 - 1 / leverage + maintenanceBuffer))
    : entry * (1 + 1 / leverage - maintenanceBuffer);
  return { value, estimated: true };
};

export const buildPositionLevels = (position, settings) => {
  if (!position) return [];
  const side = normalizeSide(position.side);
  const direction = side === "long" ? 1 : side === "short" ? -1 : 0;
  const entry = number(position.entry_price ?? position.entryPrice);
  const identity = position.id ?? `${normalizeSymbol(position.symbol)}-${side}`;
  const breakEvenPct = number(settings?.risk?.break_even_pct);
  const trailActivationPct = number(settings?.risk?.trailing_activate_pct);
  const levels = [
    { key: `entry-${identity}`, label: "ENTRY", value: entry, tone: "entry" },
    { key: `mark-${identity}`, label: "MARK", value: number(position.mark_price ?? position.markPrice), tone: "mark" },
    {
      key: `sl-${identity}`,
      label: position.trailing_active ? "TRAIL STOP" : "STOP",
      value: number(position.stop_loss ?? position.stopLoss),
      tone: "stop",
    },
    {
      key: `tp-${identity}`,
      label: "TAKE PROFIT",
      value: number(position.take_profit ?? position.takeProfit),
      tone: "take",
    },
  ];

  if (!position.break_even_moved && entry > 0 && direction && breakEvenPct > 0) {
    levels.push({
      key: `be-${identity}`,
      label: "BREAK-EVEN",
      value: entry * (1 + direction * breakEvenPct / 100),
      tone: "trigger",
    });
  }
  if (!position.trailing_active && entry > 0 && direction && trailActivationPct > 0) {
    levels.push({
      key: `trail-${identity}`,
      label: "TRAIL ACTIVE",
      value: entry * (1 + direction * trailActivationPct / 100),
      tone: "trigger",
    });
  }

  const liquidation = getLiquidationLevel(position);
  if (liquidation) {
    levels.push({
      key: `liq-${identity}`,
      label: liquidation.estimated ? "EST. LIQ" : "LIQUIDATION",
      value: liquidation.value,
      tone: "liquidation",
      estimated: liquidation.estimated,
    });
  }
  return levels.filter((level) => finite(level.value) && level.value > 0);
};

export const buildOrderLevels = (orders) =>
  (Array.isArray(orders) ? orders : [])
    .filter(isActiveOrder)
    .map((order, index) => {
      const side = normalizeSide(order.side);
      const value = [order.stop_price, order.trigger_price, order.price, order.avg_price]
        .find((candidate) => finite(candidate) && Number(candidate) > 0);
      const type = String(order.order_type || order.type || "ORDER").replaceAll("_", " ").toUpperCase();
      return {
        key: `order-${order.id ?? `${normalizeSymbol(order.symbol)}-${index}`}`,
        label: `${side === "buy" ? "BUY" : side === "sell" ? "SELL" : "ORDER"} ${type}`,
        value: number(value),
        tone: "order",
      };
    })
    .filter((level) => level.value > 0);
